import React from 'react'
import { CiEdit } from 'react-icons/ci'
import { MdPayment } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { ConvertJalaliDate } from 'utilities/helper/digitToMonth'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'

interface IItemProps {
    index: number
    jalaliDate: string
    amount: number
    isDelayed: boolean
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    setLoanAmount: React.Dispatch<React.SetStateAction<number | undefined>>
    isPayed: boolean
    setSelectedInstallment: React.Dispatch<
        React.SetStateAction<number | undefined>
    >
    id: number
    shouldShowPayButton: boolean
}

// Android item_installment.xml (force-LTR): قسط N top-RIGHT, «مبلغ قسط»
// centered under it, date on the RIGHT below the amount, a red معوق tag
// hanging from the top-center, edit pencil top-LEFT, پرداخت button
// bottom-LEFT (turns into a gray «پرداخت شده» chip once paid).
export default function Item({
    index,
    jalaliDate,
    amount,
    isDelayed,
    setIsOpen,
    setLoanAmount,
    isPayed,
    setSelectedInstallment,
    id,
    shouldShowPayButton,
}: IItemProps) {
    const { moneyPool } = useParams()
    const navigate = useNavigate()
    const { moneyPoolManagement } = useGetMoneyPoolManagement(moneyPool)

    const canEdit =
        moneyPoolManagement?.poolship?.role === 'mgr' ||
        moneyPoolManagement?.poolship?.role === 'own'

    const handleEdit = () => {
        navigate(`edit/${id}`, {
            state: {
                amount: amount,
                number: index + 1,
            },
        })
    }

    const handlePayment = () => {
        setIsOpen(true)
        setSelectedInstallment(id)
        setLoanAmount(amount)
    }

    return (
        <div
            className="relative w-full rounded-[4px] border border-[#EFEFEF] bg-white px-2.5 pt-1 pb-1.5 text-sm"
            dir="ltr"
        >
            {/* معوق tag hanging from the top-center edge */}
            {isDelayed && !isPayed && (
                <div className="absolute left-1/2 top-0 w-[65px] -translate-x-1/2 rounded-b-[4px] bg-[#E41F1F] p-0.5 text-center">
                    <span className="text-xs text-white">معوق</span>
                </div>
            )}

            {/* edit pencil — top-left, gray_100 */}
            {canEdit && !isPayed && (
                <CiEdit
                    className="absolute left-1.5 top-1.5 h-6 w-6 cursor-pointer p-[2px] text-[#565F6B]"
                    onClick={handleEdit}
                />
            )}

            {/* قسط N — top-right */}
            <p className="mt-1 text-right text-sm font-bold text-black">
                قسط {index + 1}
            </p>

            {/* مبلغ قسط — centered */}
            <p className="mt-1 text-center text-sm text-black">
                مبلغ قسط: {amount?.toLocaleString()} تومان
            </p>

            {/* date — right, under the amount */}
            <p className="mt-1 text-right text-xs text-black" dir="rtl">
                {ConvertJalaliDate(jalaliDate)}
            </p>

            {/* bottom-left: پرداخت button / پرداخت شده chip */}
            {(isPayed || shouldShowPayButton) && (
                <div className="mt-1 flex min-h-[28px] items-center">
                    {isPayed ? (
                        <span className="rounded-[4px] bg-[#EFEFEF] px-2.5 py-1 text-xs font-bold text-black">
                            پرداخت شده
                        </span>
                    ) : (
                        <button
                            className="flex w-[72px] items-center justify-center gap-1 rounded-[4px] bg-green-1 py-1 text-xs text-white active:scale-[0.98]"
                            onClick={handlePayment}
                        >
                            پرداخت
                            <MdPayment className="h-[18px] w-[18px]" />
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}
